import { useCallback, useEffect, useMemo, useRef, useState } from "react";

const STORAGE_PREFIX = "scheduleDraft.ui";

function resolveDefaultValue(defaultValue) {
    return typeof defaultValue === "function" ? defaultValue() : defaultValue;
}

function getLocalStorage() {
    if (typeof window === "undefined") {
        return null;
    }

    try {
        return window.localStorage || null;
    } catch {
        return null;
    }
}

export function buildScheduleDraftStorageKey(name, { draftId, scope = "prefs" } = {}) {
    if (!name) {
        return null;
    }

    if (scope === "draft") {
        return draftId ? `${STORAGE_PREFIX}.${draftId}.${name}` : null;
    }

    return `${STORAGE_PREFIX}.prefs.${name}`;
}

function readStorageValue(storageKey, defaultValue, validate) {
    const fallback = resolveDefaultValue(defaultValue);
    const storage = getLocalStorage();

    if (!storage || !storageKey) {
        return fallback;
    }

    try {
        const rawValue = storage.getItem(storageKey);
        if (rawValue == null) {
            return fallback;
        }

        const parsedValue = JSON.parse(rawValue);
        if (validate && !validate(parsedValue)) {
            return fallback;
        }

        return parsedValue;
    } catch {
        return fallback;
    }
}

function writeStorageValue(storageKey, value) {
    const storage = getLocalStorage();

    if (!storage || !storageKey) {
        return;
    }

    try {
        storage.setItem(storageKey, JSON.stringify(value));
    } catch {
        // Ignore quota/security errors and keep the in-memory state usable.
    }
}

export function useLocalStorage(name, defaultValue, { draftId, scope = "prefs", validate } = {}) {
    const validateRef = useRef(validate);
    validateRef.current = validate;

    const storageKey = useMemo(
        () => buildScheduleDraftStorageKey(name, { draftId, scope }),
        [draftId, name, scope],
    );
    const [value, setValueState] = useState(() => readStorageValue(storageKey, defaultValue, validateRef.current));

    useEffect(() => {
        setValueState(readStorageValue(storageKey, defaultValue, validateRef.current));
    }, [defaultValue, storageKey]);

    const setValue = useCallback(
        (nextValueOrUpdater) => {
            setValueState((currentValue) => {
                const nextValue = typeof nextValueOrUpdater === "function" ? nextValueOrUpdater(currentValue) : nextValueOrUpdater;
                writeStorageValue(storageKey, nextValue);
                return nextValue;
            });
        },
        [storageKey],
    );

    return [value, setValue, storageKey];
}

export default useLocalStorage;
